import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Send,
  Forward,
  CheckCircle,
  XCircle,
  MessageSquare,
  Clock,
  User,
  MapPin,
  Calendar,
  FileText,
  AlertTriangle,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { formatDate, formatDateTime } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Labels
const STATUS_LABELS: Record<string, string> = {
  ENTWURF: 'Entwurf',
  EINGEREICHT: 'Eingereicht',
  IN_PRUEFUNG: 'In Prüfung',
  WEITERGELEITET: 'Weitergeleitet',
  ABGESCHLOSSEN: 'Abgeschlossen',
  STORNIERT: 'Storniert',
};

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  BESONDERES_VORKOMMNIS: 'Besonderes Vorkommnis',
  AUFFAELLIGKEIT: 'Auffälligkeit/Beobachtung',
  DISZIPLINAR_ANZEIGE: 'Disziplinaranzeige',
  BESUCH_VORKOMMNIS: 'Besuchsvorkommnis',
  VERLEGUNG_ANREGUNG: 'Verlegungsanregung',
  ARBEIT_VORKOMMNIS: 'Arbeitsvorkommnis',
  EILMELDUNG: 'Eilmeldung',
};

const PRIORITY_LABELS: Record<string, string> = {
  NIEDRIG: 'Niedrig',
  NORMAL: 'Normal',
  HOCH: 'Hoch',
  EILSACHE: 'Eilsache',
};

const LOCATION_LABELS: Record<string, string> = {
  HAFTRAUM: 'Haftraum',
  GEMEINSCHAFTSRAUM: 'Gemeinschaftsraum',
  ARBEITSBEREICH: 'Arbeitsbereich',
  FREISTUNDE: 'Freistunde/Hof',
  BESUCHSBEREICH: 'Besuchsbereich',
  VERWALTUNG: 'Verwaltung',
  EINGANGSBEREICH: 'Eingangsbereich',
  SPORT: 'Sportbereich',
  SCHULE: 'Schule',
  WERKSTATT: 'Werkstatt',
  KUECHE: 'Küche',
  SONSTIGER_ORT: 'Sonstiger Ort',
};

// API Funktionen
const fetchWorkflow = async (id: string) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/api/v1/vgo-workflows/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) throw new Error('Fehler beim Laden');
  return response.json();
};

export function VgoWorkflowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasAnyRole } = useAuth();

  const isLeadership = hasAnyRole(['leitung', 'it_admin']);

  // Dialoge
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showForwardDialog, setShowForwardDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Formular-Daten
  const [submitComment, setSubmitComment] = useState('');
  const [forwardTo, setForwardTo] = useState('');
  const [forwardComment, setForwardComment] = useState('');
  const [resolution, setResolution] = useState('');
  const [newComment, setNewComment] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  // Workflow laden
  const { data: workflow, isLoading, error } = useQuery({
    queryKey: ['vgo-workflow', id],
    queryFn: () => fetchWorkflow(id!),
    enabled: !!id,
  });

  // Mutations
  const submitMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/vgo-workflows/${id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ comment: submitComment }),
      });
      if (!response.ok) throw new Error('Fehler beim Einreichen');
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Vorgang eingereicht', description: 'Der Vorgang wurde zur Prüfung eingereicht.' });
      queryClient.invalidateQueries({ queryKey: ['vgo-workflow', id] });
      setShowSubmitDialog(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    },
  });

  const forwardMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/vgo-workflows/${id}/forward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ forwardTo, comment: forwardComment }),
      });
      if (!response.ok) throw new Error('Fehler beim Weiterleiten');
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Vorgang weitergeleitet' });
      queryClient.invalidateQueries({ queryKey: ['vgo-workflow', id] });
      setShowForwardDialog(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/vgo-workflows/${id}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ resolution }),
      });
      if (!response.ok) throw new Error('Fehler beim Abschließen');
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Vorgang abgeschlossen', description: 'Der Aktenvermerk wurde erstellt.' });
      queryClient.invalidateQueries({ queryKey: ['vgo-workflow', id] });
      setShowResolveDialog(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    },
  });

  const commentMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/vgo-workflows/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content: newComment }),
      });
      if (!response.ok) throw new Error('Fehler beim Kommentieren');
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Kommentar hinzugefügt' });
      queryClient.invalidateQueries({ queryKey: ['vgo-workflow', id] });
      setShowCommentDialog(false);
      setNewComment('');
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/vgo-workflows/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reason: cancelReason }),
      });
      if (!response.ok) throw new Error('Fehler beim Stornieren');
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Vorgang storniert' });
      queryClient.invalidateQueries({ queryKey: ['vgo-workflow', id] });
      setShowCancelDialog(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    },
  });

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center">Lädt...</div>;
  }

  if (error || !workflow) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-destructive">Vorgang nicht gefunden</p>
        <Button onClick={() => navigate('/vgo-workflows')}>Zurück zur Übersicht</Button>
      </div>
    );
  }

  const canSubmit = workflow.status === 'ENTWURF';
  const canForward = ['EINGEREICHT', 'IN_PRUEFUNG'].includes(workflow.status);
  const canResolve = ['EINGEREICHT', 'IN_PRUEFUNG', 'WEITERGELEITET'].includes(workflow.status) && isLeadership;
  const canComment = !['ABGESCHLOSSEN', 'STORNIERT'].includes(workflow.status);
  const canCancel = !['ABGESCHLOSSEN', 'STORNIERT'].includes(workflow.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/vgo-workflows')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-mono">{workflow.workflowNumber}</h1>
              <Badge variant={
                workflow.status === 'ABGESCHLOSSEN' ? 'default' :
                workflow.status === 'STORNIERT' ? 'destructive' :
                workflow.status === 'ENTWURF' ? 'outline' : 'secondary'
              }>
                {STATUS_LABELS[workflow.status]}
              </Badge>
              {workflow.priority === 'EILSACHE' && (
                <Badge variant="destructive">EILSACHE</Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              {WORKFLOW_TYPE_LABELS[workflow.workflowType]}
            </p>
          </div>
        </div>

        {/* Aktionen */}
        <div className="flex gap-2">
          {canComment && (
            <Button variant="outline" onClick={() => setShowCommentDialog(true)}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Kommentar
            </Button>
          )}
          {canSubmit && (
            <Button onClick={() => setShowSubmitDialog(true)}>
              <Send className="mr-2 h-4 w-4" />
              Einreichen
            </Button>
          )}
          {canForward && (
            <Button variant="outline" onClick={() => setShowForwardDialog(true)}>
              <Forward className="mr-2 h-4 w-4" />
              Weiterleiten
            </Button>
          )}
          {canResolve && (
            <Button onClick={() => setShowResolveDialog(true)}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Abschließen
            </Button>
          )}
          {canCancel && (
            <Button variant="destructive" onClick={() => setShowCancelDialog(true)}>
              <XCircle className="mr-2 h-4 w-4" />
              Stornieren
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Hauptinhalt */}
        <div className="lg:col-span-2 space-y-6">
          {/* Sachverhalt */}
          <Card>
            <CardHeader>
              <CardTitle>Sachverhalt</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="whitespace-pre-wrap text-sm">{workflow.description}</div>
              
              {workflow.immediateMeasures && (
                <div>
                  <p className="font-medium text-sm mb-1">Erste Maßnahmen:</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {workflow.immediateMeasures}
                  </p>
                </div>
              )}

              {workflow.witnesses && (
                <div>
                  <p className="font-medium text-sm mb-1">Zeugen:</p>
                  <p className="text-sm text-muted-foreground">{workflow.witnesses}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Verlegungsdetails */}
          {workflow.workflowType === 'VERLEGUNG_ANREGUNG' && (
            <Card>
              <CardHeader>
                <CardTitle>Verlegungsanregung</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {workflow.transferType && (
                  <p><span className="font-medium">Art:</span> {workflow.transferType}</p>
                )}
                {workflow.targetFacility && (
                  <p><span className="font-medium">Zielanstalt:</span> {workflow.targetFacility}</p>
                )}
                {workflow.transferReason && (
                  <div>
                    <p className="font-medium">Begründung:</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {workflow.transferReason}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Abschlussvermerk */}
          {workflow.resolution && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Abschlussvermerk
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm">{workflow.resolution}</p>
                {workflow.recordItem && (
                  <p className="mt-4 text-sm text-muted-foreground">
                    Akteneintrag erstellt: {workflow.recordItem.title}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Historie */}
          <Card>
            <CardHeader>
              <CardTitle>Verlauf</CardTitle>
              <CardDescription>Chronologische Dokumentation aller Änderungen</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {workflow.history?.map((entry: any) => (
                  <div key={entry.id} className="flex gap-4 pb-4 border-b last:border-0">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">
                          {entry.action === 'CREATED' && 'Vorgang erstellt'}
                          {entry.action === 'STATUS_CHANGE' && `Status: ${STATUS_LABELS[entry.previousStatus]} → ${STATUS_LABELS[entry.newStatus]}`}
                          {entry.action === 'FORWARDED' && `Weitergeleitet an ${entry.details?.forwardedTo}`}
                          {entry.action === 'RESOLVED' && 'Vorgang abgeschlossen'}
                          {entry.action === 'COMMENT_ADDED' && 'Kommentar hinzugefügt'}
                          {entry.action === 'UPDATED' && 'Vorgang bearbeitet'}
                        </p>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(entry.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {entry.createdBy?.displayName || entry.createdBy?.username}
                      </p>
                      {entry.comment && (
                        <p className="text-sm mt-1 p-2 bg-muted rounded">{entry.comment}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Kommentare */}
          {workflow.comments?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Kommentare</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {workflow.comments.map((comment: any) => (
                    <div key={comment.id} className="p-3 bg-muted rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">
                          {comment.createdBy?.displayName || comment.createdBy?.username}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm">{comment.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Gefangener */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Gefangener
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Link 
                to={`/prisoners/${workflow.prisoner.id}`}
                className="text-primary hover:underline font-medium"
              >
                {workflow.prisoner.lastName}, {workflow.prisoner.firstName}
              </Link>
              <p className="text-sm text-muted-foreground">{workflow.prisoner.prisonerNumber}</p>
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Datum: {formatDate(workflow.incidentDate)}</span>
                {workflow.incidentTime && <span>({workflow.incidentTime} Uhr)</span>}
              </div>
              
              {workflow.location && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{LOCATION_LABELS[workflow.location] || workflow.location}</span>
                  {workflow.locationDetail && <span>- {workflow.locationDetail}</span>}
                </div>
              )}

              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>Erstellt: {formatDateTime(workflow.createdAt)}</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>Von: {workflow.createdBy?.displayName || workflow.createdBy?.username}</span>
              </div>

              {workflow.dueDate && (
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  <span>Frist: {formatDate(workflow.dueDate)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialoge */}
      {/* Einreichen */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vorgang einreichen</DialogTitle>
            <DialogDescription>
              Der Vorgang wird zur Prüfung eingereicht und kann danach nicht mehr bearbeitet werden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Kommentar (optional)</Label>
              <Textarea
                value={submitComment}
                onChange={(e) => setSubmitComment(e.target.value)}
                placeholder="Zusätzliche Hinweise..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>Abbrechen</Button>
            <Button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>
              Einreichen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weiterleiten */}
      <Dialog open={showForwardDialog} onOpenChange={setShowForwardDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vorgang weiterleiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Weiterleiten an *</Label>
              <Input
                value={forwardTo}
                onChange={(e) => setForwardTo(e.target.value)}
                placeholder="z.B. Sicherheitsdienst, Sozialdienst"
              />
            </div>
            <div className="space-y-2">
              <Label>Kommentar</Label>
              <Textarea
                value={forwardComment}
                onChange={(e) => setForwardComment(e.target.value)}
                placeholder="Grund der Weiterleitung..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForwardDialog(false)}>Abbrechen</Button>
            <Button onClick={() => forwardMutation.mutate()} disabled={forwardMutation.isPending || !forwardTo}>
              Weiterleiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Abschließen */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vorgang abschließen</DialogTitle>
            <DialogDescription>
              Der Vorgang wird abgeschlossen und ein Aktenvermerk wird erstellt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Abschlussvermerk *</Label>
              <Textarea
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                placeholder="Abschließende Dokumentation..."
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>Abbrechen</Button>
            <Button onClick={() => resolveMutation.mutate()} disabled={resolveMutation.isPending || resolution.length < 5}>
              Abschließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kommentar */}
      <Dialog open={showCommentDialog} onOpenChange={setShowCommentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kommentar hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Kommentar *</Label>
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ihr Kommentar..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommentDialog(false)}>Abbrechen</Button>
            <Button onClick={() => commentMutation.mutate()} disabled={commentMutation.isPending || !newComment}>
              Hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stornieren */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vorgang stornieren</DialogTitle>
            <DialogDescription>
              Der Vorgang wird storniert und kann nicht mehr bearbeitet werden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Begründung *</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Grund für die Stornierung..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending || !cancelReason}>
              Stornieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
